import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { fadeIn } from 'components/utils/animations'
import { useOrdersByDepartureStatus } from 'hooks/useOrdersByDepartureStatus'
import useExperiment from 'hooks/Experiments/useExperiment'
import { Experiments } from 'constants/experiments'
import LesaPurchaseSummary from '../../SelectPurchase/LesaPurchaseSummary/LesaPurchaseSummary'
import LesaNlsPropertyCard from '../../SelectPurchase/LesaNlsPropertyCard'
import LesaNlsPropertyCarouselCard from '../../SelectPurchase/LesaNlsPropertyCarouselCard'
import Action from './Action'
import { isChip, isComponent } from '../../../utils/actionsUtils'
import { arrayToMap } from '../../../../client/lib/array/arrayUtils'
import LesaPurchaseSummarySkeleton from '../../SelectPurchase/LesaPurchaseSummary/Common/LesaPurchaseSummarySkeleton'
import ModalContext from 'contexts/ModalContext'
import LesaOTPModal from 'supportAssistant/components/Chat/MessageMeta/LesaOTPModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getFirstName } from 'selectors/accountSelectors'
import Group from 'components/utils/Group'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'
import { getOrderBookingNumbers, getOrdersWithAirportTransfers } from 'selectors/orderSelectors'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import { hasMeaningfulNlsDescription } from 'supportAssistant/utils/nlsDescription'
import TextButton from 'LuxKit/Button/TextButton'
import { CLEAR_SUPPORT_ASSISTANT_MESSAGE_DRAFT_EVENT } from 'supportAssistant/utils/messageDraftStorage'

export const META_COMPONENTS_PADDING_INLINE_START_VAR = '--meta-components-padding-inline-start'

const Container = styled(Group)`
  padding-inline-start: var(${META_COMPONENTS_PADDING_INLINE_START_VAR}, ${rem(32)});
`

const ScrollTo = styled.div`
  padding-bottom: ${rem(4)};
  height: 0;
`

const NlsCardsContainer = styled(Group)`
  container-type: inline-size;
  container-name: nls-cards;
`

const NlsCardsCarousel = styled(CardCarousel)`
  width: 100%;
`

const AgentPillButton = styled(TextButton)`
  animation: ${fadeIn} 1s;
`

const EMPTY_AGENT_PILLS: Array<App.AgentPill> = []

interface Props {
  compact?: boolean
  metaContent: Array<App.ChatMessageMetaContent>
  agentPills?: Array<App.AgentPill>
  payload?: App.ChatMessageMetaPayload
  autoScrollEnabled?: boolean
}

function MetaComponents(props: Props) {
  const { compact = false, metaContent, agentPills, payload, autoScrollEnabled = true } = props
  const visibleAgentPills = agentPills ?? EMPTY_AGENT_PILLS

  const intl = useIntl()
  const dispatch = useAppDispatch()

  const customerOrdersData = useOrdersByDepartureStatus()
  const { loading, upcoming, previous } = customerOrdersData

  const showModal = useContext(ModalContext)

  const customerId = useAppSelector((state: App.State) => state.auth.account.memberId)
  const firstName = useAppSelector(getFirstName)
  const ordersWithAirportTransfers = useAppSelector(getOrdersWithAirportTransfers)
  const orderBookingNumbers = useAppSelector(getOrderBookingNumbers)

  const [loginSuccessful, setLoginSuccessful] = useState<boolean>(false)

  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const currentOrder = useMemo(() => [...upcoming, ...previous].find(order => order.id === payload?.id_orders), [payload?.id_orders])

  const scrollRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!autoScrollEnabled) {
      return
    }

    scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'end' })
  }, [autoScrollEnabled, loading, currentOrder, metaContent, visibleAgentPills, upcoming, previous])

  useEffect(() => {
    if (loginSuccessful && customerId && firstName) {
      const updatedPayload = {
        ...payload,
        customerId,
        firstName,
      }
      dispatch(addChatMessage(intl.formatMessage({
        id: 'FoG1Jfs8p3xt0saLq330',
        defaultMessage: 'Login successful',
        description: 'Chat message text sent when user successfully logs in via OTP',
      }), { action: 'listDefaultActions', payload: updatedPayload }))
    }
  }, [customerId, dispatch, firstName, intl, loginSuccessful, payload])

  const componentsMap = useMemo(() => arrayToMap(metaContent.filter(action => isComponent(action)), a => a.type), [metaContent])
  const actionsChips = useMemo(() => metaContent.filter(isChip), [metaContent])
  const handleAgentPillClick = useCallback((agentPill: App.AgentPill) => {
    window.dispatchEvent(new Event(CLEAR_SUPPORT_ASSISTANT_MESSAGE_DRAFT_EVENT))
    dispatch(addChatMessage(agentPill.text))
  }, [dispatch])

  const isListOrdersAction = !!componentsMap.get('list-orders')
  const isListFlightsOrdersAction = !!componentsMap.get('list-flight-orders')
  const isListOrdersWithAirportTransfersAction = !!componentsMap.get('list-orders-with-airport-transfers')
  const isListOrdersWithExtraGuestsAction = !!componentsMap.get('list-orders-extra-guests')
  const isOTPModalAction = !!componentsMap.get('otp-login')
  const isListNlsPropertiesAction = !!componentsMap.get('list-nls-properties')
  const shouldCombineActionsAndAgentPills = !isListNlsPropertiesAction && actionsChips.length > 0 && visibleAgentPills.length > 0
  const actionsForPurchaseSummary = shouldCombineActionsAndAgentPills ? [] : actionsChips
  const {
    isAnyVariant: isNlsCarouselEnabled,
    variation: nlsCarouselVariation,
  } = useExperiment(Experiments.opexLesaNlsOffersCarousel, {
    canActivate: isListNlsPropertiesAction && (payload?.nlsProperties?.length ?? 0) > 0,
  })
  const shouldUseInlineBestMatchTag = useMemo(() => {
    const properties = payload?.nlsProperties ?? []
    if (!properties.length) {
      return false
    }
    return properties.some(property => !hasMeaningfulNlsDescription(property.description))
  }, [payload?.nlsProperties])
  const nlsPropertyCards = payload?.nlsProperties?.map((property, index) => {
    if (isNlsCarouselEnabled) {
      return (
        <LesaNlsPropertyCarouselCard
          compact={compact}
          key={property.offerId}
          property={property}
          index={index}
          nlsOfferParams={payload?.nlsOfferParams}
          useInlineBestMatchTag={shouldUseInlineBestMatchTag}
          nlsCarouselVariation={nlsCarouselVariation}
        />
      )
    }
    return (
      <LesaNlsPropertyCard
        key={property.offerId}
        property={property}
        index={index}
        nlsOfferParams={payload?.nlsOfferParams}
        nlsCarouselVariation={nlsCarouselVariation}
      />
    )
  })

  const ordersWithAirportTransfersIds = ordersWithAirportTransfers.map(order => order.id)

  const upcomingOrdersWithAirportTransfers = upcoming.filter(order => ordersWithAirportTransfersIds.includes(order.id))
  const previousOrdersWithAirportTransfers = previous.filter(order => ordersWithAirportTransfersIds.includes(order.id))

  const ordersWithExtraGuestsIds = orderBookingNumbers
    .filter(order => 'canAddExtraGuests' in order && order.canAddExtraGuests)
    .map(order => order.id)

  if ((isListOrdersAction || isListFlightsOrdersAction || isListOrdersWithAirportTransfersAction || isListOrdersWithExtraGuestsAction) && loading) {
    return <>
      <LesaPurchaseSummarySkeleton />
      <ScrollTo ref={scrollRef} />
    </>
  }

  if (isOTPModalAction) {
    showModal<AccountAccessModalResult>(<LesaOTPModal email={payload?.email as string} maskedPhone={payload?.maskedPhone as string} />).then(async(result) => {
      if (!result?.loggedIn) {
        dispatch(addChatMessage(intl.formatMessage({
          id: '6wh-yA4-9NDKQxqR2yZQ',
          defaultMessage: 'Login failed',
          description: 'Chat message text sent when user login via OTP fails',
        }), { action: 'listDefaultActions', payload }))
      } else {
        setLoginSuccessful(true)
      }
    })
  }

  return (
    <Container direction="vertical" gap={16}>
      {currentOrder && (!isListOrdersAction && !isListFlightsOrdersAction && !isListNlsPropertiesAction) && <LesaPurchaseSummary actions={actionsForPurchaseSummary} componentsMap={componentsMap} payload={payload} order={currentOrder} />}
      {isListOrdersAction && (
        <Group direction="vertical" gap={8}>
          {upcoming.map((order) => (
            <LesaPurchaseSummary componentsMap={componentsMap} actions={actionsChips} key={order.id} order={order} />
          ))}
          {upcoming.length === 0 && previous.map((order) => (
            <LesaPurchaseSummary componentsMap={componentsMap} actions={actionsChips} key={order.id} order={order} isPastOrder />
          ))}
          <ScrollTo ref={scrollRef} />
        </Group>
      )}
      {isListFlightsOrdersAction && (
        <Group direction="vertical" gap={8}>
          {upcoming.filter(order => order.hasFlight).map((order) => (
            <LesaPurchaseSummary componentsMap={componentsMap} actions={actionsChips} key={order.id} order={order} />
          ))}
          {upcoming.length === 0 && previous.filter(order => order.hasFlight).map((order) => (
            <LesaPurchaseSummary componentsMap={componentsMap} actions={actionsChips} key={order.id} order={order} isPastOrder />
          ))}
          <ScrollTo ref={scrollRef} />
        </Group>
      )}
      {isListOrdersWithAirportTransfersAction && (
        <Group direction="vertical" gap={8}>
          {upcomingOrdersWithAirportTransfers?.map((order) => (
            <LesaPurchaseSummary componentsMap={componentsMap} actions={actionsChips} key={order.id} order={order} />
          ))}
          {upcomingOrdersWithAirportTransfers?.length === 0 && previousOrdersWithAirportTransfers?.map((order) => (
            <LesaPurchaseSummary componentsMap={componentsMap} actions={actionsChips} key={order.id} order={order} isPastOrder />
          ))}
          <ScrollTo ref={scrollRef} />
        </Group>
      )}
      {isListOrdersWithExtraGuestsAction && (
        <Group direction="vertical" gap={8}>
          {upcoming.filter(order => ordersWithExtraGuestsIds.includes(order.id)).map((order) => (
            <LesaPurchaseSummary componentsMap={componentsMap} actions={actionsChips} key={order.id} order={order} />
          ))}
          {upcoming.length === 0 && previous.filter(order => ordersWithExtraGuestsIds.includes(order.id)).map((order) => (
            <LesaPurchaseSummary componentsMap={componentsMap} actions={actionsChips} key={order.id} order={order} isPastOrder />
          ))}
          <ScrollTo ref={scrollRef} />
        </Group>
      )}
      {isListNlsPropertiesAction && (
        <Group fullWidth wrap="wrap" horizontalAlign="start" direction="vertical" tabletDirection="horizontal" gap={8}>
          {actionsChips.map((action, index) => (
            <Action
              action={action}
              key={`${action.text}-${index}`}
              payload={payload}
              nlsCarouselVariation={nlsCarouselVariation}
            />
          ))}
        </Group>
      )}
      {isListNlsPropertiesAction && (payload?.nlsProperties?.length ?? 0) > 0 && (
        <NlsCardsContainer direction="vertical" gap={16}>
          {isNlsCarouselEnabled && <NlsCardsCarousel
            width={compact ? `${rem(248)}` : '88vw'}
            tabletWidth={compact ? `${rem(248)}` : `${rem(340)}`}
            desktopWidth={compact ? `${rem(272)}` : `${rem(440)}`}
            gutterStyle="overflow"
            gap={compact ? 12 : 16}
            snap="start"
            pageSize="auto"
          >
            {nlsPropertyCards}
          </NlsCardsCarousel>}
          {!isNlsCarouselEnabled && nlsPropertyCards}
        </NlsCardsContainer>
      )}
      {(!currentOrder || shouldCombineActionsAndAgentPills) && !isListNlsPropertiesAction &&
        <Group fullWidth wrap="wrap" horizontalAlign="start" direction="vertical" tabletDirection="horizontal" gap={8}>
          {shouldCombineActionsAndAgentPills && visibleAgentPills.map((agentPill, index) => (
            <AgentPillButton
              key={`${agentPill.value ?? agentPill.text}-${index}`}
              kind="secondary"
              variant="dark"
              onClick={() => handleAgentPillClick(agentPill)}
              data-support-assistant-agent-pill={agentPill.value ?? agentPill.text}
            >
              {agentPill.text}
            </AgentPillButton>
          ))}
          {actionsChips.map((action, index) => (
            <Action
              action={action}
              key={`${action.text}-${index}`}
              order={currentOrder}
              payload={payload}
              nlsCarouselVariation={nlsCarouselVariation}
            />
          ))}
        </Group>}
      {visibleAgentPills.length > 0 && !shouldCombineActionsAndAgentPills && (
        <Group fullWidth wrap="wrap" horizontalAlign="start" direction="vertical" tabletDirection="horizontal" gap={8}>
          {visibleAgentPills.map((agentPill, index) => (
            <AgentPillButton
              key={`${agentPill.value ?? agentPill.text}-${index}`}
              kind="secondary"
              variant="dark"
              onClick={() => handleAgentPillClick(agentPill)}
              data-support-assistant-agent-pill={agentPill.value ?? agentPill.text}
            >
              {agentPill.text}
            </AgentPillButton>
          ))}
        </Group>
      )}
      <ScrollTo ref={scrollRef} />
    </Container>
  )
}

export default MetaComponents
